import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  RotateCcw,
  Scissors,
  UserCheck,
  UserPlus,
  XCircle,
  Info,
  ArrowRight,
} from "lucide-react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/Shared";

const COLORS = {
  blue: "#2563eb",
  green: "#22c55e",
  yellow: "#f59e0b",
  red: "#ef4444",
  slate: "#334155",
};

function MainKpi({ icon: Icon, label, value, subtitle, tone = "blue", onClick }) {
  const tones = {
    blue: "border-blue-900/80 bg-slate-900/80",
    green: "border-emerald-900/80 bg-slate-900/80",
    yellow: "border-amber-900/80 bg-slate-900/80",
    red: "border-red-900/80 bg-slate-900/80",
  };

  const iconTones = {
    blue: "bg-blue-600 text-white border-blue-600",
    green: "bg-emerald-600/20 text-emerald-400 border-emerald-700/50",
    yellow: "bg-amber-600/20 text-amber-400 border-amber-700/50",
    red: "bg-red-600/20 text-red-400 border-red-700/50",
  };

  const valueTones = {
    blue: "text-blue-300",
    green: "text-emerald-300",
    yellow: "text-amber-300",
    red: "text-red-300",
  };

  return (
    <button
      onClick={onClick}
      className={`group w-full rounded-xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:border-slate-600 hover:bg-slate-900 ${tones[tone]}`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border ${iconTones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
            {label}
          </div>

          <div className={`mt-1 font-mono-data text-3xl font-semibold ${valueTones[tone]}`}>
            {value}
          </div>

          {subtitle && (
            <div className="mt-1 text-[11px] text-slate-500">
              {subtitle}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function SmallStat({ icon: Icon, label, value, tone = "blue", onClick }) {
  const tones = {
    blue: {
      border: "border-blue-900/70",
      icon: "text-blue-400 bg-blue-950/50",
      value: "text-blue-300",
    },
    green: {
      border: "border-emerald-900/70",
      icon: "text-emerald-400 bg-emerald-950/50",
      value: "text-emerald-300",
    },
    yellow: {
      border: "border-amber-900/70",
      icon: "text-amber-400 bg-amber-950/50",
      value: "text-amber-300",
    },
    red: {
      border: "border-red-900/70",
      icon: "text-red-400 bg-red-950/50",
      value: "text-red-300",
    },
  };

  const t = tones[tone];

  return (
    <button
      onClick={onClick}
      className={`w-full rounded-lg border ${t.border} bg-slate-950/40 p-4 text-left transition hover:bg-slate-900`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <div className={`rounded-full p-1.5 ${t.icon}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>

      <div className={`mt-2 font-mono-data text-2xl font-semibold ${t.value}`}>
        {value}
      </div>
    </button>
  );
}

function SectionBox({ title, subtitle, icon: Icon, children }) {
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 shadow-lg shadow-black/10">
      <div className="mb-4 flex items-center gap-3">
        {Icon && (
          <div className="rounded-lg bg-slate-800 p-2 text-blue-400">
            <Icon className="h-4 w-4" />
          </div>
        )}
        <div>
          <h3 className="font-display text-base font-semibold text-white">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-0.5 text-[10px] text-slate-500">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {children}
    </section>
  );
}

export default function Dashboard() {
  const {
    korwilFilter,
    setKorwilFilter,
    kordaFilter,
    user,
  } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmationSummary, setConfirmationSummary] = useState({ sudah_konfirmasi: 0, belum_konfirmasi: 0 });
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);

    Promise.all([
      api.get("/dashboard/summary", {
        params: {
          korwil: korwilFilter || undefined,
        },
      }),
      api.get("/unit/summary", {
        params: {
          korwil: korwilFilter || undefined,
        },
      }),
    ])
      .then(([dashboardRes, unitRes]) => {
        setData(dashboardRes.data);
        setConfirmationSummary({
          sudah_konfirmasi: Number(unitRes.data?.sudah_konfirmasi || 0),
          belum_konfirmasi: Number(unitRes.data?.belum_konfirmasi || 0),
        });
      })
      .catch(() => {
        setData(null);
        setConfirmationSummary({ sudah_konfirmasi: 0, belum_konfirmasi: 0 });
      })
      .finally(() => setLoading(false));
  }, [korwilFilter]);

  const t = data?.totals || {};

  const donutData = useMemo(() => {
    const proses = Number(t.proses_instalasi || 0);
    const selesai = Number(t.selesai || 0);
    const kondisi = Number(t.kondisi_instalasi || 0);

    return [
      { name: "Proses Instalasi", value: proses, color: COLORS.blue },
      { name: "Instalasi Selesai", value: selesai, color: COLORS.green },
      { name: "Kondisi Instalasi", value: kondisi, color: COLORS.yellow },
    ];
  }, [t.proses_instalasi, t.selesai, t.kondisi_instalasi]);

  const korwils = data?.per_korwil || [];

  const format = (value) =>
    Number(value || 0).toLocaleString("id-ID");

  return (
    <div
      data-testid="page-dashboard"
      className="min-h-full pb-8"
    >
      <SectionTitle
        title="Command Center"
        subtitle={`${
          user?.role === "KORWIL"
            ? `KORWIL ${user.korwil} · ${kordaFilter || "semua Korda"}`
            : `KORDA ${user?.korda || kordaFilter || "SEMUA"} · ${korwilFilter || "seluruh Korwil"}`
        }`}
      />

      {/* FILTER KORWIL */}
      <div className="mb-5 flex flex-wrap gap-2">
        <button
          disabled={user?.role === "KORWIL"}
          onClick={() => {
            if (user?.role !== "KORWIL") {
              setKorwilFilter("");
            }
          }}
          className={`rounded-lg border px-3 py-1.5 text-[11px] font-semibold transition ${
            user?.role === "KORWIL"
              ? "cursor-not-allowed border-slate-800 bg-slate-950 text-slate-700 opacity-50"
              : !korwilFilter
                ? "border-blue-500 bg-blue-600 text-white"
                : "border-slate-700 bg-slate-900 text-slate-400 hover:text-white"
          }`}
        >
          SEMUA KORWIL
        </button>

        {["ARIPIN", "ADELIA", "ELSA", "VIKI"].map((k) => {
          const isKorwilUser = user?.role === "KORWIL";
          const isOwnKorwil = user?.korwil === k;
          const disabled = isKorwilUser && !isOwnKorwil;

          return (
            <button
              key={k}
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  setKorwilFilter(korwilFilter === k ? "" : k);
                }
              }}
              className={`rounded-lg border px-3 py-1.5 text-[11px] font-semibold transition ${
                disabled
                  ? "cursor-not-allowed border-slate-800 bg-slate-950 text-slate-700 opacity-50"
                  : korwilFilter === k || (isKorwilUser && isOwnKorwil)
                    ? "border-blue-500 bg-blue-600 text-white"
                    : "border-slate-700 bg-slate-900 text-slate-400 hover:text-white"
              }`}
            >
              {k}
            </button>
          );
        })}
      </div>

      {/* MAIN KPI */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MainKpi
          icon={ClipboardList}
          label="Total Alokasi"
          value={format(t.total_unit)}
          subtitle="Total jadwal dari pusat"
          tone="blue"
          onClick={() => nav("/unit")}
        />

        <MainKpi
          icon={Scissors}
          label="Proses Instalasi"
          value={format(t.proses_instalasi)}
          subtitle="Belum selesai, termasuk yang sedang dikerjakan"
          tone="blue"
          onClick={() => nav("/unit")}
        />

        <MainKpi
          icon={CheckCircle2}
          label="Instalasi Selesai"
          value={format(t.selesai)}
          subtitle={`${t.progress ?? 0}% dari total alokasi`}
          tone="green"
          onClick={() => nav("/unit?ops_status=SELESAI")}
        />

        <MainKpi
          icon={AlertTriangle}
          label="Kondisi Instalasi"
          value={format(t.kondisi_instalasi)}
          subtitle={`Terkendala ${format(t.terkendala)} · Reschedule ${format(t.reschedule)} · Gagal ${format(t.gagal_instalasi)}`}
          tone="yellow"
          onClick={() => nav("/unit")}
        />
      </div>

      {/* CONFIRMATION + INSTALLATION CONDITION */}
      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SectionBox
          title="Konfirmasi Jadwal"
          subtitle="Ringkasan status konfirmasi jadwal instalasi"
          icon={CheckCircle2}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SmallStat
              icon={CheckCircle2}
              label="Sudah Konfirmasi"
              value={format(confirmationSummary.sudah_konfirmasi)}
              tone="green"
              onClick={() => nav("/unit?ops_status=TERKONFIRMASI")}
            />

            <SmallStat
              icon={AlertTriangle}
              label="Belum Konfirmasi"
              value={format(confirmationSummary.belum_konfirmasi)}
              tone="yellow"
              onClick={() => nav("/unit?ops_status=BELUM_DIKONFIRMASI")}
            />
          </div>
        </SectionBox>

        <SectionBox
          title="Kondisi Instalasi"
          subtitle="Unit yang membutuhkan perhatian atau tindak lanjut"
          icon={AlertTriangle}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <SmallStat
              icon={Info}
              label="Terkendala"
              value={format(t.terkendala)}
              tone="yellow"
              onClick={() => nav("/unit?ops_status=TERKENDALA")}
            />

            <SmallStat
              icon={RotateCcw}
              label="Reschedule"
              value={format(t.reschedule)}
              tone="yellow"
              onClick={() => nav("/unit?ops_status=RESCHEDULE")}
            />

            <SmallStat
              icon={XCircle}
              label="Gagal Instalasi"
              value={format(t.gagal_instalasi)}
              tone="red"
              onClick={() => nav("/unit?ops_status=GAGAL_INSTALASI")}
            />
          </div>
        </SectionBox>
      </div>

      {/* ANALYTICS */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SectionBox
          title="Proporsi Status"
          subtitle="Distribusi progres seluruh alokasi"
        >
          <div className="grid min-h-[300px] grid-cols-1 items-center md:grid-cols-2">
            <div className="relative h-[270px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={72}
                    outerRadius={105}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {donutData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={entry.color}
                      />
                    ))}
                  </Pie>

                  <Tooltip
                    contentStyle={{
                      background: "#0f172a",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                      fontSize: 12,
                    }}
                    formatter={(value, name) => [
                      format(value),
                      name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>

              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="font-mono-data text-3xl font-semibold text-white">
                    {format(t.total_unit)}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Total Alokasi
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4 px-2">
              {donutData.map((item) => {
                const percentage = t.total_unit
                  ? ((item.value / t.total_unit) * 100).toFixed(1)
                  : "0.0";

                return (
                  <div key={item.name}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-sm"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-xs text-slate-400">
                          {item.name}
                        </span>
                      </div>

                      <span className="font-mono-data text-xs text-slate-200">
                        {format(item.value)}
                      </span>
                    </div>

                    <div className="mt-1 text-right text-[10px] text-slate-600">
                      {percentage}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionBox>

        <SectionBox
          title="Progress per Korwil"
          subtitle="Perbandingan total alokasi dan instalasi selesai"
        >
          <div className="min-h-[300px] space-y-5 py-2">
            {loading && (
              <div className="py-20 text-center text-xs text-slate-500">
                Memuat data...
              </div>
            )}

            {!loading && korwils.length === 0 && (
              <div className="py-20 text-center text-xs text-slate-500">
                Belum ada data Korwil.
              </div>
            )}

            {!loading &&
              korwils.map((k) => (
                <button
                  key={k.korwil}
                  onClick={() =>
                    setKorwilFilter(
                      korwilFilter === k.korwil ? "" : k.korwil
                    )
                  }
                  className="group w-full text-left"
                >
                  <div className="mb-1.5 flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="rounded border border-blue-900/70 bg-blue-950/40 px-2 py-0.5 text-[10px] font-bold text-blue-300">
                        {k.korwil}
                      </span>
                      <span className="truncate text-[10px] text-slate-500">
                        {format(k.total_unit)} unit
                      </span>
                    </div>

                    <div className="font-mono-data text-[11px] text-slate-300">
                      {format(k.selesai)} selesai · {k.progress ?? 0}%
                    </div>
                  </div>

                  <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-500"
                      style={{
                        width: `${Math.min(Number(k.progress || 0), 100)}%`,
                      }}
                    />
                  </div>

                  <div className="mt-1 flex justify-between text-[9px] text-slate-600">
                    <span>
                      {format(k.selesai)} selesai
                    </span>
                    <span>
                      {format(Math.max((k.total_unit || 0) - (k.selesai || 0), 0))} tersisa
                    </span>
                  </div>
                </button>
              ))}
          </div>

          <div className="mt-3 flex justify-end border-t border-slate-800 pt-3">
            <button
              onClick={() => nav("/performance")}
              className="flex items-center gap-1 text-[10px] font-semibold text-blue-400 hover:text-blue-300"
            >
              Lihat Performance
              <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </SectionBox>
      </div>

      {/* FOOTER INFO */}
      <div className="mt-5 flex items-center justify-between border-t border-slate-800 pt-3 text-[9px] text-slate-600">
        <span>
          PYX KORDA CONTROL CENTER
        </span>

        <span>
          Data dashboard berasal dari alokasi pusat & update Korda
        </span>
      </div>
    </div>
  );
}
